/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { gnoment } from 'shared-ui/gnoment';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';

const iso8601DateFormat = 'YYYY-MM-DD';

export function toLocalDate(input) {
    let date = input;
    if (input instanceof Date) {
        date = gnoment().set(input);
    } else if (!gnoment.isGnoment(input)) {
        throw new Error('Invalid parameter.  Required: gnoment or Date.');
    }
    return date.format(iso8601DateFormat);
}

export function toGnoment(localDate) {
    return gnoment(localDate, iso8601DateFormat);
}

export function localDateNow() {
    return toLocalDate(gnoment());
}

export function toAmPmTime(militaryTime) {
    let result = militaryTime;
    if (/^\d{4}$/.test(militaryTime)) {
        // If exactly 4 digits
        let hours = parseInt(militaryTime.substring(0, 2), 10);
        hours %= 24; //
        const ampm = hours < 12 ? ' AM' : ' PM';
        if (hours > 12) {
            hours -= 12;
        } else if (hours === 0) {
            hours = 12;
        }
        result = `${hours}:${militaryTime.substring(2)}${ampm}`;
    }
    return result;
}

const duckDate = (date = '') =>
    typeof date === 'string' ? date.replace(/T.*/, '') : date;

function isIn(period, date) {
    const start = gnoment(duckDate(period.start_date)).startOf('day');
    const end = gnoment(duckDate(period.end_date)).startOf('day');
    const test = gnoment(duckDate(date)).startOf('day');
    return start.isSameOrBefore(test) && end.isSameOrAfter(test);
}

function is(freeDay, date) {
    const day = gnoment(duckDate(freeDay.date)).startOf('day');
    const test = gnoment(duckDate(date)).startOf('day');
    return day.isSame(duckDate(test));
}

function findPricingDivision(period, date) {
    const day = gnoment(duckDate(date)).startOf('day');
    const start = gnoment(duckDate(period.start_date)).startOf('day');
    if (start.isAfter(day)) {
        return false;
    }

    const monthDayString = day.format('MM-DD');

    for (const division of period.divisions) {
        if (division.starts_at <= monthDayString && division.ends_at >= monthDayString) {
            return division;
        }
    }

    return false;
}

export function findPricingPeriod(periods, date) {
    const day = gnoment(duckDate(date)).startOf('day');
    let period = false;
    let currentStart = gnoment('0001-01-01T00:00:00Z');
    for (const p of periods) {
        const pStart = gnoment(duckDate(p.start_date)).startOf('day');
        if (pStart.isSameOrBefore(day) && pStart.isAfter(currentStart)) {
            period = p;
            currentStart = pStart;
        }
    }
    return period;
}

export function isFreeDay(facility, date) {
    const freeDays = get(facility, 'free_days') || [];
    return freeDays.reduce(
        (acc, freeDay) =>
            acc ||
            (freeDay.end
                ? isIn({ start_date: freeDay.date, end_date: freeDay.end }, date)
                : is(freeDay, date)),
        false
    );
}

export function isBlackoutDate(facility, date) {
    const periods = get(facility, 'blackout_periods') || [];
    return periods.reduce((acc, period) => acc || isIn(period, date), false);
}

export function datesBlocked(facility, passType) {
    return (date) => {
        const pricingPeriod = findPricingPeriod(passType.periods, date);
        if (!pricingPeriod) {
            return true;
        }
        const pricingDivision = findPricingDivision(pricingPeriod, date);
        if (!pricingDivision) {
            return true;
        }

        return isBlackoutDate(facility, date) || isFreeDay(facility, date);
    };
}

/** Booking window in months by agency (used for NYR / not-yet-released). */
export const BOOKING_WINDOWS_AGENCY_MAP = {
    BLM: 6,
    FWS: 6,
    USACE: 6,
    NPS: 1,
    USFS: 1,
};

/** Facility-specific booking window overrides (months). */
export const FACILITY_BOOKING_WINDOW_EXCEPTIONS = {
    74291: 6, // Rocky Mountain National Park
    74271: 6, // Acadia National Park
    74396: 6, // Snake River SitePass
};

export function isBeforeToday(date) {
    const day = gnoment(duckDate(date)).startOf('day');
    return day.isBefore(gnoment().startOf('day'));
}

export function isBeyondBookingWindow(facility, date) {
    const months =
        FACILITY_BOOKING_WINDOW_EXCEPTIONS[facility?.id] ??
        BOOKING_WINDOWS_AGENCY_MAP[facility?.agency] ??
        6;
    const day = gnoment(duckDate(date)).startOf('day');
    return day.isAfter(gnoment().add(months, 'months'));
}

export function isPassTypeUnavailableForDate(facility, passType, date) {
    if (!facility || !passType || !date) {
        return true;
    }
    return (
        datesBlocked(facility, passType)(date) ||
        isBeforeToday(date) ||
        isBeyondBookingWindow(facility, date)
    );
}

/**
 * Whether any pass type can be purchased for the given date.
 * Defaults to true when facility/pass-type data is not loaded yet.
 */
export function isSitePassPurchasableOnDate(facility, passTypes, date) {
    if (!facility?.id || !Array.isArray(passTypes) || passTypes.length === 0 || !date) {
        return true;
    }
    return passTypes.some(
        (passType) => !isPassTypeUnavailableForDate(facility, passType, date)
    );
}

export function getDivisionFromPeriods(periods, date) {
    try {
        return findPricingDivision(findPricingPeriod(periods, date), date);
    } catch (e) {
        return {};
    }
}

export function getValidPriceFromDivision(periods, date) {
    try {
        const sortedPeriods = sortBy(periods, 'start_date');
        let division = findPricingDivision(findPricingPeriod(sortedPeriods, date), date);
        if (!division) {
            division = sortedPeriods[sortedPeriods.length - 1].divisions[0];
        }
        return division.display_price;
    } catch (e) {
        return {};
    }
}

export function expandBack(divisions, div, startMonthDay) {
    if (div.starts_at === '01-01' && div.ends_at !== '12-31') {
        for (const division of divisions) {
            if (
                division.ends_at === '12-31' &&
                division.starts_at !== '01-01' &&
                div.name === division.name &&
                div.price === division.price
            ) {
                return [division, true];
            }
        }
    }
    // support for seasons across years
    if (
        div.starts_at > div.ends_at &&
        startMonthDay !== undefined &&
        startMonthDay <= div.ends_at
    ) {
        return [div, true];
    }

    return [div, false];
}

export function expandForward(divisions, div, startMonthDay) {
    if (div.starts_at !== '01-01' && div.ends_at === '12-31') {
        for (const division of divisions) {
            if (
                division.starts_at === '01-01' &&
                division.ends_at !== '12-31' &&
                division.name === div.name &&
                division.price === div.price
            ) {
                return [division, true];
            }
        }
    }
    // support for seasons across years
    if (
        div.starts_at > div.ends_at &&
        startMonthDay !== undefined &&
        startMonthDay > div.ends_at
    ) {
        return [div, true];
    }

    return [div, false];
}

export const isDailyPassType = (passType = '') => passType?.includes('day');
